import { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import { Button, Spinner } from '@follow/ui';

import AssetsService from '../../services/AssetsService';
import TeamService from '../../services/TeamService';
import { LocationRole, TeamMember, TeamMemberStatus } from '../../services/models/team';
import { ApiError } from '../../types/common';
import useLocationRole from '../../hooks/useLocationRole';
import ModalInviteTeammate from '../../components/Modals/ModalInviteTeammate';
import ModalConfirmDelete from '../../components/Modals/ModalConfirmDelete';

interface TeamRow {
  key: string;
  // Absent on the owner row: the owner is not a membership record.
  memberId?: number;
  role: LocationRole;
  status: TeamMemberStatus;
  name: string;
  email: string;
  initials: string;
  isYou: boolean;
  rentalEnabled: boolean;
}

const displayName = (firstName?: string | null, lastName?: string | null) => (
  `${firstName ?? ''} ${lastName ?? ''}`.trim()
);

const initialsOf = (name: string, email: string) => {
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (email.slice(0, 2) || '?').toUpperCase();
};

const columnHeaderClass = 'typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40';

const Team = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const queryClient = useQueryClient();

  const { user, canManage, isLoading: isAccessLoading } = useLocationRole(siteId);

  const [filter, setFilter] = useState('');
  const [inviteVisible, setInviteVisible] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<TeamRow>();
  const [updatingMemberId, setUpdatingMemberId] = useState<number>();

  const { data: team, isPending: isTeamPending, isError: isTeamError } = useQuery({
    // The key MUST use the string siteId everywhere: React Query compares keys
    // structurally, so ['team', 12] would not invalidate ['team', '12'].
    queryKey: ['team', siteId],
    queryFn: () => TeamService.getTeam(siteId!),
    enabled: !!siteId && canManage,
  });

  const { data: location } = useQuery({
    queryKey: ['location', siteId],
    queryFn: () => AssetsService.getLocation(Number(siteId)),
    enabled: !!siteId,
    select: (data) => data.location,
  });

  // The team query stays disabled until the access block says the viewer may
  // manage it — a disabled query reports `isPending`, so both have to settle
  // before the table means anything.
  const isLoading = isAccessLoading || (canManage && isTeamPending && !isTeamError);

  const invalidateTeam = () => {
    queryClient.invalidateQueries({ queryKey: ['team', siteId] });
  };

  const updateMemberMutation = useMutation({
    mutationFn: ({ memberId, rentalEnabled }: { memberId: number; rentalEnabled: boolean }) =>
      TeamService.updateMember(siteId!, memberId, rentalEnabled),
    onSuccess: (_data, variables) => {
      toast.success(variables.rentalEnabled ? 'Accès aux réservations activé.' : 'Accès aux réservations retiré.');
      invalidateTeam();
      // The teammate's own sidebar reads the access block.
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (error: ApiError) => {
      toast.error(error.message ?? 'Erreur lors de la mise à jour du membre.');
    },
    onSettled: () => setUpdatingMemberId(undefined),
  });

  const resendMutation = useMutation({
    mutationFn: (memberId: number) => TeamService.resendInvite(siteId!, memberId),
    onSuccess: () => {
      toast.success('Invitation renvoyée.');
    },
    onError: (error: ApiError) => {
      if (error.code === 'RESEND_TOO_SOON') {
        toast.error('Invitation déjà renvoyée récemment. Réessayez dans quelques minutes.');
        return;
      }
      toast.error(error.message ?? 'Erreur lors de l\'envoi de l\'invitation.');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (memberId: number) => TeamService.removeMember(siteId!, memberId),
    onSuccess: (_data, memberId) => {
      const wasPending = memberToRemove?.memberId === memberId
        && memberToRemove?.status === TeamMemberStatus.PENDING;
      toast.success(wasPending ? 'Invitation annulée.' : 'Membre retiré de l\'équipe.');
      invalidateTeam();
    },
    onError: (error: ApiError) => {
      toast.error(error.message ?? 'Erreur lors de la suppression.');
    },
    onSettled: () => setMemberToRemove(undefined),
  });

  // The team endpoint returns memberships only — the owner is rendered from the
  // location (or from the viewer, who is the owner in the common case).
  const ownerRow = useMemo<TeamRow>(() => {
    const viewerIsOwner = user?.access?.locations
      .some((entry) => String(entry.locationId) === String(siteId) && entry.isOwner) ?? false;
    // Prefer the owner the team endpoint returns; it is the only source that
    // works when an ADMIN views someone else's team.
    const owner = team?.owner ?? location?.user ?? (viewerIsOwner ? user : undefined);
    const name = displayName(owner?.firstName, owner?.lastName);
    const email = owner?.email ?? '';

    return {
      key: 'owner',
      role: LocationRole.OWNER,
      status: TeamMemberStatus.ACTIVE,
      name: name || (email ? '' : 'Propriétaire du site'),
      email,
      initials: initialsOf(name, email),
      isYou: viewerIsOwner,
      rentalEnabled: team?.ownerRentalEnabled ?? false,
    };
  }, [team?.owner, location?.user, user, siteId, team?.ownerRentalEnabled]);

  const memberRows = useMemo<TeamRow[]>(() => {
    const toRow = (member: TeamMember): TeamRow => {
      const name = displayName(member.user?.firstName, member.user?.lastName);
      const email = member.user?.email ?? '';
      return {
        key: `member-${member.id}`,
        memberId: member.id,
        role: LocationRole.MEMBER,
        status: member.status,
        name,
        email,
        initials: initialsOf(name, email),
        isYou: member.user != null && String(member.user.id) === String(user?.id),
        rentalEnabled: member.rentalEnabled,
      };
    };

    // Active first, then pending; alphabetical within each group.
    return (team?.members ?? [])
      .map(toRow)
      .sort((a, b) => {
        if (a.status !== b.status) return a.status === TeamMemberStatus.ACTIVE ? -1 : 1;
        return (a.name || a.email).localeCompare(b.name || b.email);
      });
  }, [team?.members, user?.id]);

  const rows = useMemo(() => {
    const all = [ownerRow, ...memberRows];
    const needle = filter.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((row) => (
      row.name.toLowerCase().includes(needle) || row.email.toLowerCase().includes(needle)
    ));
  }, [ownerRow, memberRows, filter]);

  const existingEmails = useMemo(() => (
    [ownerRow.email, ...memberRows.map((row) => row.email)]
      .filter(Boolean)
      .map((email) => email.toLowerCase())
  ), [ownerRow.email, memberRows]);

  const onToggleRentals = (row: TeamRow, checked: boolean) => {
    if (row.memberId == null) return;
    setUpdatingMemberId(row.memberId);
    updateMemberMutation.mutate({ memberId: row.memberId, rentalEnabled: checked });
  };

  const renderRentalsCell = (row: TeamRow) => {
    if (row.role === LocationRole.OWNER) {
      return <span className="typo-small text-neutral-60">Accès complet</span>;
    }

    // Not a disabled checkbox on purpose: an unchecked box reads as "off but you
    // could turn it on", when in fact the feature does not exist on this site.
    if (!team?.ownerRentalEnabled) {
      return (
        <span
          className="typo-small text-neutral-40"
          title="Les réservations ne sont pas activées sur le compte du propriétaire : aucun membre ne peut y accéder."
        >
          Non disponible
        </span>
      );
    }

    return (
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-neutral-20 text-primary-500 focus:ring-primary-500 cursor-pointer disabled:cursor-wait"
          checked={row.rentalEnabled}
          disabled={updatingMemberId === row.memberId}
          onChange={(e) => onToggleRentals(row, e.target.checked)}
        />
        <span className="typo-small text-neutral-60">
          {row.rentalEnabled ? 'Autorisé' : 'Non autorisé'}
        </span>
      </label>
    );
  };

  const renderActionsCell = (row: TeamRow) => {
    if (row.role === LocationRole.OWNER || row.memberId == null) return null;

    if (row.status === TeamMemberStatus.PENDING) {
      return (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => resendMutation.mutate(row.memberId!)}
            // Only the row being resent is busy, not every pending invite.
            disabled={resendMutation.isPending && resendMutation.variables === row.memberId}
          >
            Renvoyer
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setMemberToRemove(row)}>
            Annuler
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setMemberToRemove(row)}>
          Retirer
        </Button>
      </div>
    );
  };

  // Direct-URL protection: only the site owner (or an admin) manages the team.
  if (!isAccessLoading && !canManage) {
    return <Navigate to={`/site/${siteId}`} replace />;
  }

  return (
    <div className="w-full max-w-[1280px] px-4 flex flex-col gap-4 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-neutral typo-h1">Équipe</h1>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtrer les membres"
            className="h-8 w-56 px-3 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-white typo-small text-neutral focus:outline-primary-500"
          />
          <Button
            variant="primary"
            size="sm"
            tone="light"
            icon={<UserPlusIcon className="w-4 h-4" />}
            onClick={() => setInviteVisible(true)}
          >
            Inviter des membres
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            {/* Column headers */}
            <div className="flex items-center px-5 py-2 border-b border-neutral-10">
              <span className={`flex-grow ${columnHeaderClass}`}>Membre</span>
              <span className={`w-44 flex-shrink-0 ${columnHeaderClass}`}>Réservations</span>
              <span className={`w-32 flex-shrink-0 ${columnHeaderClass}`}>Rôle</span>
              <span className="w-52 flex-shrink-0" />
            </div>

            {isLoading ? (
              <div className="flex justify-center py-10 text-neutral-40">
                <Spinner size={20} />
              </div>
            ) : rows.length === 0 ? (
              <div className="py-8 text-center typo-small text-neutral-40">
                {filter.trim()
                  ? 'Aucun membre ne correspond à votre recherche'
                  : 'Aucun membre pour le moment. Invitez vos premiers coéquipiers.'}
              </div>
            ) : (
              rows.map((row) => {
                const pending = row.status === TeamMemberStatus.PENDING;
                return (
                  <div
                    key={row.key}
                    className="flex w-full items-center px-5 py-2.5 border-b border-neutral-5 last:border-b-0"
                  >
                    <div className={`flex-grow min-w-0 pr-2 flex items-center gap-3 ${pending ? 'opacity-60' : ''}`}>
                      <div className="w-9 h-9 flex-shrink-0 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="typo-small text-primary-600 !font-bold">{row.initials}</span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="typo-body text-neutral !font-semibold line-clamp-1">
                            {row.name || row.email || '—'}
                          </p>
                          {row.isYou && (
                            <span className="px-1.5 rounded bg-neutral-5 outline outline-1 outline-offset-[-1px] outline-neutral-20 typo-small text-neutral-60">
                              VOUS
                            </span>
                          )}
                        </div>
                        <p className="typo-small text-neutral-60 line-clamp-1">
                          {pending ? `${row.email} · Invitation en attente` : row.email}
                        </p>
                      </div>
                    </div>
                    <div className={`w-44 flex-shrink-0 ${pending ? 'opacity-60' : ''}`}>
                      {renderRentalsCell(row)}
                    </div>
                    <span className={`w-32 flex-shrink-0 typo-small text-neutral-60 ${pending ? 'opacity-60' : ''}`}>
                      {row.role === LocationRole.OWNER ? 'Propriétaire' : 'Membre'}
                    </span>
                    <div className="w-52 flex-shrink-0">
                      {renderActionsCell(row)}
                    </div>
                  </div>
                );
              })
            )}

            {/* The owner row is always there, so the "no rows" branch above only
                ever fires on a filter — this covers the real empty team. */}
            {!isLoading && memberRows.length === 0 && !filter.trim() && (
              <div className="py-8 text-center typo-small text-neutral-40 border-t border-neutral-5">
                Vous êtes seul sur ce site. Invitez vos premiers coéquipiers.
              </div>
            )}
          </div>
        </div>
      </div>

      {siteId && (
        <ModalInviteTeammate
          visible={inviteVisible}
          siteId={siteId}
          ownerRentalEnabled={team?.ownerRentalEnabled ?? false}
          existingEmails={existingEmails}
          onClose={() => setInviteVisible(false)}
        />
      )}

      <ModalConfirmDelete
        visible={memberToRemove != null}
        title={memberToRemove?.status === TeamMemberStatus.PENDING
          ? 'Annuler cette invitation'
          : 'Retirer ce membre'}
        description={memberToRemove?.status === TeamMemberStatus.PENDING
          ? `L'invitation envoyée à ${memberToRemove?.email} sera annulée. Le lien reçu par email ne fonctionnera plus.`
          : `${memberToRemove?.name || memberToRemove?.email} perdra immédiatement l'accès à ce site. Vous pourrez l'inviter à nouveau plus tard.`}
        confirmLabel={memberToRemove?.status === TeamMemberStatus.PENDING ? 'Annuler l\'invitation' : 'Retirer'}
        onCancel={() => setMemberToRemove(undefined)}
        onConfirm={() => {
          if (memberToRemove?.memberId != null) {
            removeMutation.mutate(memberToRemove.memberId);
          }
        }}
      />
    </div>
  );
};

export default Team;
